import { twClassMerge } from '@healthhub/react-ui';
import type { UploadFile } from 'antd';
import { Typography, Upload } from 'antd';
import { useState } from 'react';

import { Button } from '@shared/components';

import CustomFileInputOptionsModal from './modals/CustomFileInputOptionsModal';

const { Text } = Typography;

interface CustomFileInputProps {
  acceptedFileTypes: string;
  className?: string;
  description?: string;
  errorMsg?: string;
  handleBeforeUpload?: (file: UploadFile) => boolean | typeof Upload.LIST_IGNORE;
  handleChange?: (info: any) => void;
  handleRemove: () => void;
  inputClassName?: string;
  isDisabled?: boolean;
  isError?: boolean;
  isShowUploadList?: boolean;
  maxCount?: number;
  modalCancelText: string;
  modalTitle: string;
  removeText: string;
  replaceText: string;
  required?: boolean;
  selectedFile: null | UploadFile;
  titleText?: string;
  titleTextClassName?: string;
  updateText: string;
  uploadInputId: string;
  uploadText: string;
}

const CustomFileInput = ({
  acceptedFileTypes,
  className,
  description,
  errorMsg,
  handleBeforeUpload,
  handleChange,
  handleRemove,
  inputClassName,
  isDisabled,
  isError = false,
  isShowUploadList = false,
  maxCount = 1,
  modalCancelText,
  modalTitle,
  removeText,
  replaceText,
  required,
  selectedFile,
  titleText,
  titleTextClassName,
  updateText,
  uploadInputId,
  uploadText,
}: CustomFileInputProps) => {
  const [isShowOptionsModal, setIsShowOptionsModal] = useState(false);
  const handleCancel = () => {
    setIsShowOptionsModal(false);
  };

  const handleRemovePhoto = () => {
    handleRemove();
    setIsShowOptionsModal(false);
  };

  const handleReplacePhoto = () => {
    document.getElementById(uploadInputId)?.click();
    setIsShowOptionsModal(false);
  };

  return (
    <div className={twClassMerge('flex flex-col', className)}>
      <div
        className={twClassMerge(
          'flex flex-row border border-functional-stone rounded-lg px-4 h-14 text-base items-center',
          isError && 'border border-error-color',
          isDisabled && 'border-functional-slate bg-functional-stone',
          inputClassName
        )}
      >
        <div className="'flex flex-col' w-4/5">
          {!!titleText && (
            <Text
              className={twClassMerge(
                `${selectedFile?.name ? 'text-sm' : 'text-base'} text-functional-iron`,
                isDisabled ? 'text-secondary-grey-steel' : 'text-functional-iron',
                titleTextClassName
              )}
            >
              {!!required && <Text className="text-error-color">*</Text>}
              {titleText}
            </Text>
          )}
          {!!selectedFile?.name && (
            <div className="flex items-center">
              <Text className="text-sm gbmask mr-2 truncate text-base font-semibold">{selectedFile?.name}</Text>
            </div>
          )}
        </div>

        <div className="w-1/5 text-end">
          <Upload
            accept={acceptedFileTypes}
            beforeUpload={handleBeforeUpload}
            maxCount={maxCount}
            onChange={handleChange}
            showUploadList={isShowUploadList}
          >
            <Button
              customDisabledStyle="bg-transparent"
              disabled={isDisabled}
              hidden={!!selectedFile}
              id={uploadInputId}
              variant="text"
            >
              <Text
                className={twClassMerge(
                  'text-sm font-semibold ',
                  isDisabled ? 'text-secondary-grey-steel' : 'text-theme-600'
                )}
              >
                {uploadText}
              </Text>
            </Button>
          </Upload>
          {!!selectedFile && (
            <Button id={`update-${uploadInputId}`} onClick={() => setIsShowOptionsModal(true)} variant="text">
              <Text className="text-sm font-semibold text-theme-600">{updateText}</Text>
            </Button>
          )}
        </div>
      </div>

      {!!isError && <Text className="text-sm mt-2 block text-xs text-error-color">{errorMsg}</Text>}
      {!isError && !!description && (
        <Text className="text-sm mt-2 block text-xs text-functional-iron">{description}</Text>
      )}

      <CustomFileInputOptionsModal
        cancelText={modalCancelText}
        handleCancel={handleCancel}
        handleRemovePhoto={handleRemovePhoto}
        handleReplacePhoto={handleReplacePhoto}
        isShowOptionsModal={isShowOptionsModal}
        onCancel={() => setIsShowOptionsModal(false)}
        removeText={removeText}
        replaceText={replaceText}
        title={modalTitle}
      />
    </div>
  );
};

export default CustomFileInput;
